import OSS from 'ali-oss'
import AWS from 'aws-sdk'
import request from './request'
import { Loading } from 'element-ui'
import { v4 as uuidv4 } from 'uuid'
import { api } from '~/ui-domain'
import { decorType } from '@/utils/decor-types'


export default class TSTUpload {
  constructor() {
    this.tokenApi = `${api[decorType]}/${decorType}/uploaders/sts-token`
    this.loading = ''
    this.tokenRes = null
    this.aliOSSClient = null
    this.minioClient = null
  }

  /**
   * 上传方法
   * @param {Object} params
   * @param {File} params.file 文件
   * @param {string} params.filename 文件名
   * @param {Function} params.progress 进度回调
   * @param {number} params.partSize 分片大小，默认100KB，最小100KB
   * @param {boolean} params.loading 是否显示loading遮罩
   * @returns {Promise<{name, url: *}>}
   */
  async upload(params) {
    if (!params.partSize) {
      params.partSize = 102400
    }
    if (!params.filename) {
      params.filename = uuidv4() + TSTUpload.getFileExt(params.file.name)
    }
    if (params.loading !== false) {
      this.loading = Loading.service({
        lock: true,
        background: 'rgba(0, 0, 0, 0.7)',
        spinner: 'el-icon-loading',
        text: '请稍候...'
      })
    }
    try {
      await this.getSTSToken()
      const { plugin_id } = this.tokenRes
      switch (plugin_id) {
        case 'ossPlugin':
          return await this.uploadToAliOSS(params)
        case 'minIOPlugin':
          return await this.uploadToMinio(params)
        case 'localPlugin':
          return await this.uploadToDefault(params)
      }
    } catch (e) {
      return Promise.reject(e)
    } finally {
      if (this.loading) {
        this.loading.close()
        this.loading = null
      }
    }
  }

  /**
   * 获取Token
   * @returns {Promise<void>}
   */
  async getSTSToken() {
    let apiLoading = null
    if (!this.loading) {
      apiLoading = Loading.service({
        lock: true,
        background: 'rgba(0, 0, 0, 0.7)',
        spinner: 'el-icon-loading',
        text: '请稍候...'
      })
    }
    try {
      this.tokenRes = await request({ url: this.tokenApi, needToken: true })
    } catch (e) {
      return Promise.reject(e)
    } finally {
      if (apiLoading) {
        apiLoading.close()
        apiLoading = null
      }
    }
  }

  /**
   * 上传到阿里云OSS
   * @param filename
   * @param file
   * @param progress
   * @param partSize
   * @returns {Promise<Awaited<{name, url: *, file}>>}
   */
  async uploadToAliOSS({ filename, file, progress, partSize }) {
    if (!this.aliOSSClient) {
      const { endpoint, access_key_id, access_key_secret, security_token, bucket } = this.tokenRes
      this.aliOSSClient = new OSS({
        region: endpoint.split('.aliyuncs.com')[0],
        accessKeyId: access_key_id,
        accessKeySecret: access_key_secret,
        stsToken: security_token,
        authorizationV4: true,
        bucket: bucket,
        refreshSTSToken: async() => {
          await this.getSTSToken()
          return {
            accessKeyId: this.tokenRes.access_key_id,
            accessKeySecret: this.tokenRes.access_key_secret,
            stsToken: this.tokenRes.security_token
          }
        }
      })
    }
    const filePath = `${this.tokenRes.pic_location}/${filename}`.replace(/\/\//g, '/')
    const res = await this.aliOSSClient.multipartUpload(filePath, file, {
      partSize,
      progress: (p) => {
        if (typeof progress === 'function') {
          progress(p)
        }
      }
    })
    if (!res || !res.res || res.res.statusCode !== 200 || !res.res.requestUrls[0]) {
      return Promise.reject(res)
    }
    if (!res.res.requestUrls || !Array.isArray(res.res.requestUrls)) {
      return Promise.reject(res)
    }
    const [url] = res.res.requestUrls
    return Promise.resolve({ name: res.name, url: url.split('?')[0], file })
  }

  /**
   * 上传到Minio
   * @param filename
   * @param file
   * @param progress
   * @param partSize
   * @returns {Promise<unknown>}
   */
  async uploadToMinio({ filename, file, progress, partSize }) {
    const { endpoint, access_key_id, access_key_secret, security_token, bucket } = this.tokenRes
    if (!this.minioClient) {
      this.minioClient = new AWS.S3({
        accessKeyId: access_key_id,
        secretAccessKey: access_key_secret,
        sessionToken: security_token,
        endpoint,
        s3ForcePathStyle: true,
        signatureVersion: 'v4'
      })
    }
    const params = { Bucket: bucket, Key: filename, Body: file }
    return new Promise((resolve, reject) => {
      // Minio的默认最小分片是5MB
      partSize = partSize < 5242880 ? 5242880 : partSize
      const managedUpload = this.minioClient.upload(params, { partSize }, (err) => {
        if (err) return reject(err)
        if (typeof progress === 'function') {
          progress(1)
        }
        const url = `${endpoint}/${bucket}/${filename}`
        resolve({ name: filename, url, file })
      })
      managedUpload.on('httpUploadProgress', (progressEvent) => {
        if (typeof progress !== 'function') return
        const percentCompleted = Math.round(progressEvent.loaded / progressEvent.total)
        progress(percentCompleted)
      })
    })
  }

  /**
   * 上传到服务器
   * @param filename
   * @param file
   * @param progress
   * @param partSize
   * @returns {Promise<Awaited<{name, url, file}>>}
   */
  async uploadToDefault({ filename, file, progress }) {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await request({
        url: `${api[decorType]}/${decorType}/uploaders`,
        method: 'post',
        onUploadProgress: (progressEvent) => {
          if (typeof progress !== 'function') return
          const percentCompleted = Math.round(progressEvent.loaded / progressEvent.total)
          progress(percentCompleted)
        },
        data: formData
      })
      return Promise.resolve({ name: filename, url: res.url, file })
    } catch (e) {
      return Promise.reject(e)
    }
  }

  /**
   * 获取文件名后缀
   * @param filename
   * @returns {*}
   */
  static getFileExt(filename) {
    if (!filename || typeof filename !== 'string') return ''
    if (filename.indexOf('.') === -1) return ''
    const ext = filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
    return `.${ext}`
  }
}

const tstUpload = new TSTUpload()
export { tstUpload }
